import { isArray, isUndef } from '@lhb/func';

/** AI 评价输入的对比点位 */
export interface AIComparePoint {
  pointId: number | string;
  name?: string;
  [key: string]: any;
}

/**
 * AI 评价使用的 8 个指标
 * label 与百炼智能体提示词中的指标名称保持一致，unit 用于拼接可读数值
 */
export const AI_METRIC_FIELDS = [
  { key: 'cpmMS', label: 'CPM', unit: '元/千人次' },
  { key: 'paybackMonthsMS', label: '回本周期', unit: '个月' },
  { key: 'rentSalesRatioMS', label: '租售比', unit: '%' },
  { key: 'netMarginMS', label: '净利率', unit: '%' },
  { key: 'monthlySalesEstMS', label: '月预估销售额', unit: '元' },
  { key: 'dailyTrafficMS', label: '日均过店客流', unit: '人次/日' },
  { key: 'monthlyRentAndFees', label: '月度租金及费用', unit: '元' },
  { key: 'synergyCoefMS', label: '协同系数', unit: '' },
] as const;

/**
 * @description 判断指标是否缺失
 * null、undefined、空字符串、NaN 均视为缺失；0 是有效值
 */
export function isMissingMetric(value: any) {
  return isUndef(value) || value === '' || (typeof value === 'number' && Number.isNaN(value));
}

/**
 * @description 计算点位缺失指标数
 * CPM 与日均过店客流同时缺失时，两者合计按 1 个计
 */
export function getMissingMetricCount(point: AIComparePoint) {
  let count = AI_METRIC_FIELDS.reduce((total, field) => total + (isMissingMetric(point[field.key]) ? 1 : 0), 0);
  if (isMissingMetric(point.cpmMS) && isMissingMetric(point.dailyTrafficMS)) {
    count -= 1;
  }
  return Math.max(count, 0);
}

/**
 * @description 格式化传给 AI 的指标值
 * 缺失值返回 null，有值指标拼接单位，保留接口返回的小数位
 */
export function formatAIMetricValue(value: any, unit: string) {
  if (isMissingMetric(value)) return null;
  const numberValue = Number(value);
  const displayValue = Number.isNaN(numberValue) ? String(value) : String(numberValue);
  return `${displayValue}${unit}`;
}

/**
 * @description 构造对比组全部点位的指标 JSON 入参
 */
export function buildAIEvaluationInput(points: AIComparePoint[]) {
  return points.map((point) => {
    const input: Record<string, any> = {
      pointId: String(point.pointId),
      name: point.name || '',
      missingMetricCount: getMissingMetricCount(point),
    };
    AI_METRIC_FIELDS.forEach((field) => {
      input[field.label] = formatAIMetricValue(point[field.key], field.unit);
    });
    return input;
  });
}

/**
 * @description 从 Responses API 返回体中取出最终输出文本
 */
export function extractAIResponseText(response: any) {
  const output = isArray(response?.output) ? response.output : [];
  const message = output.find((item: any) => item?.type === 'message' && item?.role === 'assistant') ||
    output.find((item: any) => item?.type === 'message');
  const content = isArray(message?.content) ? message.content : [];
  const textBlock = content.find((item: any) => item?.type === 'output_text' && typeof item?.text === 'string');
  if (!textBlock) throw new Error('AI 响应缺少输出文本');
  return textBlock.text;
}

/**
 * @description 解析模型返回的 JSON 数组，兼容 ```json 代码块和前后附加说明
 * @return pointId -> comment
 */
export function parseAIComments(text: string) {
  const rawText = String(text || '').trim();
  if (!rawText) throw new Error('AI 响应为空');
  const fenced = rawText.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = (fenced ? fenced[1] : rawText).trim();
  let parsed: any;
  try {
    parsed = JSON.parse(candidate);
  } catch {
    const start = candidate.indexOf('[');
    const end = candidate.lastIndexOf(']');
    if (start === -1 || end <= start) throw new Error('AI 响应不是合法 JSON');
    parsed = JSON.parse(candidate.slice(start, end + 1));
  }
  const list = isArray(parsed) ? parsed : isArray(parsed?.comments) ? parsed.comments : [];
  return list.reduce((comments: Record<string, string>, item: any) => {
    if (item && !isUndef(item.pointId) && item.pointId !== '' && typeof item.comment === 'string' && item.comment.trim()) {
      comments[String(item.pointId)] = item.comment.trim();
    }
    return comments;
  }, {});
}

/**
 * @description 从完整响应中解析 pointId -> comment
 */
export function parseAIEvaluationResponse(response: any) {
  return parseAIComments(extractAIResponseText(response));
}
